import { Outlet, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Languages } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { homePathFor } from '../lib/roles';

interface AuthLayoutProps {
  /** When true (default), signed-in users are sent to their dashboard. */
  guestOnly?: boolean;
}

export function AuthLayout({ guestOnly = true }: AuthLayoutProps) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();

  if (guestOnly && isAuthenticated && user) {
    return <Navigate to={homePathFor(user.role)} replace />;
  }

  const points = [t('auth.brandPoint1'), t('auth.brandPoint2'), t('auth.brandPoint3')];

  return (
    <div className="flex min-h-screen bg-white">
      {/* Brand panel */}
      <aside className="relative hidden w-[44%] max-w-xl flex-col justify-between overflow-hidden bg-navy-900 p-12 text-white lg:flex">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -end-32 -top-32 h-96 w-96 rounded-full bg-primary-500/20 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-40 -start-24 h-96 w-96 rounded-full bg-primary-400/10 blur-3xl"
        />

        <div className="relative">
          <div className="inline-flex rounded-xl bg-white px-4 py-2.5 shadow-sm">
            <img src="/images/pharmatec-logo.png" alt="PharmaTec" className="h-9" />
          </div>
        </div>

        <div className="relative">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            {t('auth.brandHeadline')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-navy-200">{t('auth.brandSubtitle')}</p>
          <ul className="mt-8 space-y-4">
            {points.map((point) => (
              <li key={point} className="flex items-start gap-3 text-sm text-navy-100">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary-400" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-navy-300">
          &copy; {new Date().getFullYear()} PharmaTec. {t('common.allRightsReserved')}
        </p>
      </aside>

      {/* Form panel */}
      <main className="flex flex-1 flex-col">
        <div className="flex items-center justify-between px-6 py-5 sm:px-10">
          <img src="/images/pharmatec-logo.png" alt="PharmaTec" className="h-8 lg:invisible" />
          <button
            type="button"
            onClick={() => i18n.changeLanguage(i18n.language === 'ar' ? 'fr' : 'ar')}
            className="btn-ghost btn-sm"
          >
            <Languages className="h-4 w-4" />
            {i18n.language === 'ar' ? 'Français' : 'العربية'}
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 pb-12 sm:px-10">
          <div className="w-full max-w-md">
            <Outlet />
          </div>
        </div>

        <p className="pb-6 text-center text-xs text-gray-400 lg:hidden">
          &copy; {new Date().getFullYear()} PharmaTec. {t('common.allRightsReserved')}
        </p>
      </main>
    </div>
  );
}
